// @ts-strict-ignore
import ReactDomServer from 'react-dom/server';
import { stripTags } from 'underscore.string';
import { debounce } from 'lodash';
import { RefObject } from 'react';
import { getMention } from 'src/utils/getElement/getMention';
import MentionsPortal from 'src/components/textEditor/components/suggestedMentionsPortal';
import { Mention } from 'src/utils/hooks/useFetchMentions';

const MAX_USERNAME_LENGTH = 20;
const ADD_NEW_MENTION = /(?:^| )@([^ \n]*)$/; // determine if the last "word" start with a `@`
const MAX_MENTIONED_USERS = 15;

type Props = {
  mentionedUsers: Mention[];
  searchText: string;
  setMentionedUsers: (mentionedUsers: Mention[]) => void;
  setSearchText: (searchText: string) => void;
  setTarget: (target: Range | null) => void;
  suggestedMentions: Mention[];
  target: Range | null;
}
export default function Mentions({
  mentionedUsers,
  searchText,
  setMentionedUsers,
  setSearchText,
  setTarget,
  suggestedMentions,
  target,
}: Props) {

  const insertMention = (_: unknown, user: Mention) => {
    if (!isMentioning({ searchText, suggestedMentions })) return;
    const selection = window.getSelection();

    if (selection && selection.rangeCount) {
      const range = selection.getRangeAt(0);
      const node = range.endContainer;
      const offset = range.endOffset;
      range.setStart(node, Math.max(offset - searchText?.length - 1, 0));
      range.deleteContents();

      const parser = document.createElement('div');
      // needs to add blank space before else in some browsers, the insertion removes the text just before
      parser.innerHTML = '&nbsp;' + ReactDomServer.renderToStaticMarkup(getMention(user, true)) + '&nbsp;';

      let newNode = parser.firstChild;
      while (newNode) {
        range.insertNode(newNode);
        range.setStartAfter(newNode);
        range.setEndAfter(newNode);
        selection.removeAllRanges();
        selection.addRange(range);
        newNode = parser.firstChild;
      }
      setMentionedUsers([...mentionedUsers, user]);
      setSearchText('');
      setTarget(null);
    }
  };


  return (
    <>
      {target && isMentioning({ searchText, suggestedMentions }) && (
        <MentionsPortal
          addMention={insertMention}
          isCanAdd={mentionedUsers?.length < MAX_MENTIONED_USERS}
          target={target}
          users={suggestedMentions}
        />
      )}
    </>
  );
}
export function isMentioning({ searchText, suggestedMentions }: { searchText?: string, suggestedMentions: Mention[] }) {
  return searchText?.length > 0 && suggestedMentions?.length > 0;
}

type CaptureMention = {
  target: Range | null;
  setTarget: (target: Range | null) => void;
  setSearchText: (searchText: string) => void;
  isFocused: boolean;
}
function captureMention({ target, setTarget, setSearchText, isFocused }: CaptureMention) {
  const selection = window.getSelection();
  const node = selection?.anchorNode as HTMLElement;
  const offset = selection?.anchorOffset ?? 0;

  if (!selection?.isCollapsed || !node || node.nodeName === 'A' || node.nodeName === 'BUTTON' || !isFocused) {
    setSearchText('');
    setTarget(null);
    return;
  }
  const str = node.innerHTML || node.nodeValue;

  if (!str) {
    setSearchText('');
    setTarget(null);
    return;
  }
  const maxString = str.slice(Math.max(offset - MAX_USERNAME_LENGTH - 1, 0), offset);
  const mention = ADD_NEW_MENTION.exec(maxString.replace(/\s/g, ' '));
  if (!target && mention) {
    let newTarget = selection.getRangeAt(0);
    newTarget = newTarget.cloneRange(); // necessary for chrome - otherwise, keeps selecting selection, and range is not correct
    newTarget.setStart(newTarget.startContainer, 0);
    setTarget(newTarget);
  }
  setSearchText(mention?.[1] ?? '');
}

export function debouncedCaptureMentionFactory({ target, setTarget, setSearchText, isFocused }: CaptureMention) {
  return debounce(() => captureMention({ target, setTarget, setSearchText, isFocused }), 200);
}

type GetMentionedUsers = {
  editorRef: RefObject<HTMLDivElement>,
  setMentionedUsers: (mentionedUsers: Mention[]) => void,
}
export function getMentionedUsers({ editorRef, setMentionedUsers }: GetMentionedUsers) {
  if (!editorRef.current || !editorRef.current.getElementsByTagName) return;

  const mentionedUsers = Array.from(editorRef.current.getElementsByTagName('button'))
    .map((link) => ({
      userId: Number(link.getAttribute('data-id')),
      username: stripTags(link.innerHTML),
    }))
    .filter(user => !!user.userId);

  setMentionedUsers(mentionedUsers || []);
  return mentionedUsers;
}
